import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import {
  Search, ClipboardList, Calendar, CalendarDays, X, User, Building2, Phone, Mail,
  MapPin, Clock, FileDown, StickyNote, Navigation, MessageSquare, FileText,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import SkeletonCard from '@/components/ui/SkeletonCard';
import PageHeader from '@/components/ui/PageHeader';
import { format, isToday, isPast, parseISO, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  fetchPdfSettings, renderCompanyHeader, renderFooter,
  wrapPdfDocument, downloadPdfFromHtml, escapeHtml,
} from '@/lib/pdfRenderer';

// Zeitfilter: mehrfach kombinierbar (Standard: Heute + Überfällig)
const TIME_FILTERS = [
  { key: 'heute', label: 'Heute' },
  { key: 'ueberfaellig', label: 'Überfällig' },
  { key: 'kommend', label: 'Kommend' },
  { key: 'erledigt', label: 'Erledigt' },
];

const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const TASK_TYPE_ICONS = {
  'Besuchen': Navigation,
  'Erstbesuch': MapPin,
  'Anrufen': Phone,
  'Angebot': FileText,
  'Angebot senden': FileText,
  'Nachfassen': Clock,
  'Lieferung': Building2,
  'Bedarfsabfrage': MessageSquare,
};

// Zeitliche Einstufung einer offenen Aufgabe
function classifyTask(task) {
  if (task.status === 'Abgeschlossen') return 'erledigt';
  if (!task.due_date) return 'kommend';
  const due = parseISO(task.due_date);
  if (isToday(due)) return 'heute';
  if (isPast(due)) return 'ueberfaellig';
  return 'kommend';
}

function fmtTime(t) {
  if (!t || t.length < 4) return '';
  return `${t.slice(0, 2)}:${t.slice(2)}`;
}

// Öffnungszeiten des HEUTIGEN Wochentags (Google-Places-Format)
function todayOpeningHours(hoursData) {
  if (!hoursData) return null;
  const day = new Date().getDay();
  // Bevorzugt: weekday_text (fertig formatiert, Index 0 = Montag)
  if (Array.isArray(hoursData.weekday_text) && hoursData.weekday_text.length === 7) {
    const idx = (day + 6) % 7;
    return hoursData.weekday_text[idx];
  }
  if (!Array.isArray(hoursData.periods) || hoursData.periods.length === 0) return null;
  const todays = hoursData.periods.filter((p) => p.open && p.open.day === day);
  if (todays.length === 0) return `${WEEKDAYS_DE[day]}: Geschlossen`;
  if (todays.length === 1 && todays[0].open.time === '0000' && !todays[0].close) {
    return `${WEEKDAYS_DE[day]}: 24h geöffnet`;
  }
  const ranges = todays
    .map((p) => `${fmtTime(p.open.time)}–${p.close ? fmtTime(p.close.time) : ''}`)
    .join(', ');
  return `${WEEKDAYS_DE[day]}: ${ranges}`;
}

export default function AdminTasks() {
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilters, setTimeFilters] = useState(['heute', 'ueberfaellig']);
  const [agentFilter, setAgentFilter] = useState('all');
  // Datumsfilter: EIN Kalender — 1. Klick = Startdatum, 2. Klick = Enddatum.
  // Nur ein Klick (oder zweimal derselbe Tag) = einzelnes Datum.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const autoExpandedRef = React.useRef(false);

  const { data: tasks = [], isLoading, error: tasksError } = useQuery({
    queryKey: ['admin-all-tasks'],
    queryFn: () => base44.entities.Task.list('due_date'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['admin-leads-for-tasks'],
    queryFn: () => base44.entities.Lead.list('name'),
  });

  const leadById = useMemo(() => {
    const map = {};
    leads.forEach((l) => { map[l.id] = l; });
    return map;
  }, [leads]);

  const toggleTimeFilter = (key) => {
    setTimeFilters((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Mindestens ein Filter bleibt aktiv
      return next.length === 0 ? prev : next;
    });
  };

  // Datumsfilter aktiv, sobald ein Datum gesetzt ist
  const dateFilterActive = !!dateFrom;

  // Kalender-Klick: from → to → neu beginnen
  const handleCalendarSelect = (day) => {
    const picked = format(day, 'yyyy-MM-dd');
    if (!dateFrom || (dateFrom && dateTo)) {
      setDateFrom(picked);
      setDateTo('');
    } else {
      setDateTo(picked);
      setCalendarOpen(false);
    }
  };

  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };

  const dateButtonLabel = !dateFrom
    ? 'Datum'
    : !dateTo || dateTo === dateFrom
      ? format(parseISO(dateFrom), 'dd.MM.yyyy', { locale: de })
      : `${format(parseISO(dateFrom < dateTo ? dateFrom : dateTo), 'dd.MM.', { locale: de })} – ${format(parseISO(dateFrom < dateTo ? dateTo : dateFrom), 'dd.MM.yyyy', { locale: de })}`;

  // Leere Standardansicht vermeiden: Wenn heute nichts fällig/überfällig ist,
  // einmalig automatisch auch "Kommend" einblenden.
  React.useEffect(() => {
    if (autoExpandedRef.current || tasks.length === 0 || dateFilterActive) return;
    const isDefault = timeFilters.length === 2
      && timeFilters.includes('heute') && timeFilters.includes('ueberfaellig');
    if (!isDefault) return;
    const hasCurrent = tasks.some((t) => ['heute', 'ueberfaellig'].includes(classifyTask(t)));
    const hasUpcoming = tasks.some((t) => classifyTask(t) === 'kommend');
    if (!hasCurrent && hasUpcoming) {
      autoExpandedRef.current = true;
      setTimeFilters(['heute', 'ueberfaellig', 'kommend']);
    }
  }, [tasks, timeFilters, dateFilterActive]);

  // Zeitzonensicher: Datums-Anteil in LOKALER Zeit (nicht UTC-Slice)
  const localDueDay = (raw) => {
    if (!raw) return '';
    if (raw.length <= 10) return raw;
    const d = parseISO(raw);
    return isNaN(d) ? raw.slice(0, 10) : format(d, 'yyyy-MM-dd');
  };

  // Fälligkeitsdatum im gewählten Datum/Zeitraum?
  const matchesDateFilter = (t) => {
    if (!dateFilterActive) return true;
    if (!t.due_date) return false;
    const due = localDueDay(t.due_date);
    if (!dateTo || dateTo === dateFrom) return due === dateFrom;
    // Rückwärts gewählte Reihenfolge wird toleriert
    const lo = dateFrom < dateTo ? dateFrom : dateTo;
    const hi = dateFrom < dateTo ? dateTo : dateFrom;
    return due >= lo && due <= hi;
  };

  const normAgent = (s) => (s || '').trim().toLowerCase();

  const filteredTasks = useMemo(() => {
    return tasks
      // Bei aktivem Datumsfilter dürfen die Zeit-Chips (Heute/Überfällig/
      // Kommend) den gewählten Zeitraum NICHT maskieren — sonst bleibt z. B.
      // eine Zukunftswoche leer, weil "Kommend" nicht angehakt ist.
      // Nur "Erledigt" bleibt als bewusste Einblendung wirksam.
      .filter((t) => {
        const cls = classifyTask(t);
        if (dateFilterActive) {
          return cls !== 'erledigt' || timeFilters.includes('erledigt');
        }
        return timeFilters.includes(cls);
      })
      .filter((t) => agentFilter === 'all' || normAgent(t.assigned_agent) === normAgent(agentFilter))
      .filter(matchesDateFilter)
      .filter((t) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return t.lead_name?.toLowerCase().includes(q) || t.task_type?.toLowerCase().includes(q);
      })
      // Aktualität zuerst: Heute → Überfällig (neueste zuerst, März ans
      // Ende) → Kommend (nächste zuerst) → Erledigt (zuletzt erledigte oben)
      .sort((a, b) => {
        const ORDER = { heute: 0, ueberfaellig: 1, kommend: 2, erledigt: 3 };
        const ca = classifyTask(a), cb = classifyTask(b);
        if (ORDER[ca] !== ORDER[cb]) return ORDER[ca] - ORDER[cb];
        const da = a.due_date || '', db = b.due_date || '';
        if (ca === 'ueberfaellig') return db.localeCompare(da); // frisch überfällig oben
        if (ca === 'erledigt') {
          return (b.completed_date || db).localeCompare(a.completed_date || da);
        }
        return (da || '9999').localeCompare(db || '9999'); // heute/kommend aufsteigend
      });
  }, [tasks, timeFilters, agentFilter, searchTerm, dateFrom, dateTo]);

  // Agentenliste: Groß-/Kleinschreibung-tolerant dedupliziert
  const agents = useMemo(() => {
    const seen = new Map();
    tasks.forEach((t) => {
      const key = normAgent(t.assigned_agent);
      if (key && !seen.has(key)) seen.set(key, t.assigned_agent.trim());
    });
    return [...seen.values()].sort();
  }, [tasks]);
  const overdueCount = tasks.filter((t) => t.status === 'Offen' && classifyTask(t) === 'ueberfaellig').length;
  const todayCount = tasks.filter((t) => t.status === 'Offen' && classifyTask(t) === 'heute').length;

  // ── PDF-Export der aktuell gefilterten Liste ────────────────────
  const exportPdf = async () => {
    if (filteredTasks.length === 0) {
      toast.info('Keine Aufgaben zum Exportieren.');
      return;
    }
    setExporting(true);
    try {
      const settings = await fetchPdfSettings();
      const filterLabel = TIME_FILTERS
        .filter((f) => timeFilters.includes(f.key))
        .map((f) => f.label)
        .join(' + ');
      const agentLabel = agentFilter === 'all' ? 'Alle Agenten' : agentFilter;
      const dateLabel = dateFilterActive ? ` · Zeitraum: ${dateButtonLabel}` : '';

      const rows = filteredTasks.map((task) => {
        const lead = leadById[task.lead_id] || {};
        const due = task.due_date ? format(parseISO(task.due_date), 'dd.MM.yyyy', { locale: de }) : '–';
        const overdue = classifyTask(task) === 'ueberfaellig';
        const hours = (task.task_type === 'Besuchen' || task.task_type === 'Erstbesuch')
          ? todayOpeningHours(task.lead_opening_hours || lead.opening_hours)
          : null;
        const kontaktdaten = [
          task.lead_address || [lead.strasse, [lead.plz, lead.ort].filter(Boolean).join(' ')].filter(Boolean).join(', '),
          lead.telefon ? `Tel: ${lead.telefon}` : null,
          lead.email || null,
        ].filter(Boolean).join('<br/>');
        const done = task.status === 'Abgeschlossen';
        const notiz = [
          task.info_block,
          task.notes,
          done && task.ergebnis && task.ergebnis !== task.notes ? `Ergebnis: ${task.ergebnis}` : null,
          done && task.completed_date ? `Erledigt am ${format(parseISO(task.completed_date), 'dd.MM.yyyy', { locale: de })}` : null,
        ].filter(Boolean).join(' — ');
        return `<tr>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;${overdue ? 'color:#dc2626;font-weight:600;' : ''}">${due}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;"><b>${escapeHtml(task.lead_name || '–')}</b>${hours ? `<br/><span style="color:#475569;">${escapeHtml(hours)}</span>` : ''}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;">${escapeHtml(task.task_type || '–')}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:9px;color:#475569;">${kontaktdaten || '–'}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;font-size:9px;">${escapeHtml(notiz) || '–'}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;">${escapeHtml(task.assigned_agent || '–')}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;">${overdue ? 'Überfällig' : escapeHtml(task.status)}</td>
        </tr>`;
      }).join('');

      const body = `
        ${renderCompanyHeader(settings)}
        <h1 style="font-size:16px;margin:16px 0 4px;">Aufgabenliste</h1>
        <p style="font-size:10px;color:#64748b;margin:0 0 12px;">
          Stand: ${format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })} Uhr ·
          Filter: ${escapeHtml(filterLabel)} · ${escapeHtml(agentLabel)}${escapeHtml(dateLabel)} ·
          ${filteredTasks.length} Aufgabe${filteredTasks.length === 1 ? '' : 'n'}
        </p>
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <thead>
            <tr style="background:#f1f5f9;text-align:left;">
              <th style="padding:6px 8px;">Fällig</th>
              <th style="padding:6px 8px;">Kontakt</th>
              <th style="padding:6px 8px;">Aufgabe</th>
              <th style="padding:6px 8px;">Kontaktdaten</th>
              <th style="padding:6px 8px;">Notiz</th>
              <th style="padding:6px 8px;">Agent</th>
              <th style="padding:6px 8px;">Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        ${renderFooter(settings)}
      `;
      const html = wrapPdfDocument(body, 'Aufgabenliste');
      await downloadPdfFromHtml(html, `Aufgabenliste_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('Aufgabenliste als PDF exportiert');
    } catch (err) {
      console.warn('[AdminTasks] PDF export failed:', err);
      toast.error('PDF-Export fehlgeschlagen: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <PageHeader
              icon={<ClipboardList className="w-5 h-5" />}
              title="Aufgaben"
              subtitle={
                <>
                  {todayCount} heute fällig
                  {overdueCount > 0 && <span className="text-red-500"> • {overdueCount} überfällig</span>}
                </>
              }
            />
            <Button
              onClick={exportPdf}
              disabled={exporting || filteredTasks.length === 0}
              variant="outline"
              className="min-h-[44px]"
              aria-label="Gefilterte Aufgabenliste als PDF exportieren"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {exporting ? 'Exportiere…' : 'PDF-Export'}
            </Button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Zeitraum: mehrfach kombinierbar */}
              <div className="flex gap-1.5" role="group" aria-label="Zeitraum-Filter">
                {TIME_FILTERS.map((f) => {
                  const active = timeFilters.includes(f.key);
                  // Bei aktivem Datumsfilter steuern nur noch "Erledigt"
                  // und der Zeitraum die Liste — die Zeit-Chips dimmen.
                  const bypassed = dateFilterActive && f.key !== 'erledigt';
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggleTimeFilter(f.key)}
                      aria-pressed={active}
                      title={bypassed ? 'Datumsfilter aktiv — Zeitraum bestimmt die Liste' : undefined}
                      className={cn(
                        'px-3 min-h-[44px] rounded-lg text-sm font-medium border transition-colors',
                        active
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
                        bypassed && 'opacity-40'
                      )}
                    >
                      {f.label}
                      {f.key === 'ueberfaellig' && overdueCount > 0 && (
                        <span className={cn('ml-1.5 text-xs', active ? 'text-red-200' : 'text-red-500')}>
                          {overdueCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-full sm:w-48 min-h-[44px]">
                  <SelectValue placeholder="Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Agenten</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Datumsfilter: EIN Kalender — 1. Klick Start, 2. Klick Ende */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Datum oder Zeitraum wählen"
                    className={cn(
                      'flex items-center gap-2 px-3 min-h-[44px] rounded-lg text-sm font-medium border transition-colors',
                      dateFilterActive
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    <CalendarDays className="w-4 h-4" />
                    {dateButtonLabel}
                    {dateFilterActive && (
                      <X
                        className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); clearDateFilter(); }}
                        aria-label="Datumsfilter löschen"
                      />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="end">
                  <CalendarPicker
                    mode="single"
                    selected={dateTo ? parseISO(dateTo) : dateFrom ? parseISO(dateFrom) : undefined}
                    defaultMonth={dateFrom ? parseISO(dateFrom) : new Date()}
                    range={{
                      from: dateFrom ? parseISO(dateFrom) : null,
                      to: dateTo ? parseISO(dateTo) : null,
                    }}
                    onSelect={handleCalendarSelect}
                  />
                  <div className="flex items-center justify-between px-2 pb-1 pt-2 border-t border-slate-100 text-xs text-slate-500">
                    <span>
                      {dateFrom && !dateTo
                        ? 'Enddatum wählen — oder denselben Tag erneut'
                        : '1. Klick: Start · 2. Klick: Ende'}
                    </span>
                    {dateFilterActive && (
                      <button type="button" onClick={clearDateFilter} className="text-slate-600 hover:text-slate-900 font-medium">
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredTasks.map((task) => {
            const lead = leadById[task.lead_id] || {};
            const zeitStatus = classifyTask(task);
            const isDone = zeitStatus === 'erledigt';
            const isOverdue = zeitStatus === 'ueberfaellig';
            const isDueToday = zeitStatus === 'heute';
            const dueDate = task.due_date ? parseISO(task.due_date) : null;
            const TaskIcon = TASK_TYPE_ICONS[task.task_type] || ClipboardList;
            const isVisit = task.task_type === 'Besuchen' || task.task_type === 'Erstbesuch';
            const hours = isVisit ? todayOpeningHours(task.lead_opening_hours || lead.opening_hours) : null;
            const address = task.lead_address
              || [lead.strasse, [lead.plz, lead.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');
            const notiz = [task.info_block, task.notes].filter(Boolean).join(' — ');

            return (
              <Card
                key={task.id}
                className={cn(
                  'p-4',
                  isDone && 'border-l-4 border-l-emerald-500 bg-slate-50/60',
                  isOverdue && 'border-l-4 border-l-red-500 bg-red-50/40',
                  isDueToday && 'border-l-4 border-l-amber-500'
                )}
              >
                {/* Kopf: Kontakt + Aufgabenart + Status */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={cn(
                      'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                      isOverdue ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-700'
                    )}>
                      <TaskIcon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{task.lead_name || '–'}</p>
                      <p className="text-sm text-slate-500">{task.task_type}</p>
                    </div>
                  </div>
                  <StatusBadge status={isOverdue ? 'Überfällig' : task.status} type="task" />
                </div>

                {/* Besuchen: Öffnungszeiten des heutigen Tages */}
                {hours && (
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                      {hours}
                    </span>
                  </div>
                )}
                {isVisit && !hours && (
                  <p className="text-xs text-slate-400 mb-2">Keine Öffnungszeiten hinterlegt</p>
                )}

                {/* Notiz / Beschreibung */}
                {notiz && (
                  <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg p-2 mb-2">
                    <StickyNote className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                    <span className="line-clamp-3">{notiz}</span>
                  </div>
                )}

                {/* Erledigt: Ergebnis + Abschlussdatum */}
                {isDone && (
                  <div className="text-sm bg-emerald-50 border border-emerald-100 rounded-lg p-2 mb-2 space-y-0.5">
                    {task.ergebnis && (
                      <p className="text-emerald-800 font-medium">Ergebnis: {task.ergebnis}</p>
                    )}
                    {task.completed_date && (
                      <p className="text-xs text-emerald-700">
                        Erledigt am {format(parseISO(task.completed_date), 'dd.MM.yyyy HH:mm', { locale: de })} Uhr
                      </p>
                    )}
                    {!task.ergebnis && !task.completed_date && (
                      <p className="text-emerald-800">Abgeschlossen</p>
                    )}
                  </div>
                )}

                {/* Kontaktdaten */}
                <div className="space-y-1 text-sm text-slate-600 mb-3">
                  {address && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 hover:text-blue-600"
                    >
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                      <span className="truncate">{address}</span>
                    </a>
                  )}
                  {lead.telefon && (
                    <a href={`tel:${lead.telefon}`} className="flex items-center gap-2 hover:text-blue-600">
                      <Phone className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                      {lead.telefon}
                    </a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-2 hover:text-blue-600">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                      <span className="truncate">{lead.email}</span>
                    </a>
                  )}
                </div>

                {/* Fuß: Fälligkeit + Agent */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className={cn(
                    'flex items-center gap-1.5 text-sm',
                    isOverdue ? 'text-red-600 font-medium' : isDueToday ? 'text-amber-600 font-medium' : 'text-slate-500'
                  )}>
                    <Calendar className="w-3.5 h-3.5" />
                    {dueDate ? format(dueDate, 'dd.MM.yyyy', { locale: de }) : '–'}
                    {isDueToday && ' (Heute)'}
                  </div>
                  {task.assigned_agent && (
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {task.assigned_agent}
                    </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {tasksError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-700">
            <b>Aufgaben konnten nicht geladen werden:</b> {tasksError.message}
          </div>
        )}
        {isLoading && filteredTasks.length === 0 && <SkeletonCard count={4} />}
        {!isLoading && filteredTasks.length === 0 && (
          <EmptyState
            icon={ClipboardList}
            title="Keine Aufgaben"
            description="Für die gewählten Filter (Zeitraum/Agent) sind keine offenen Aufgaben vorhanden."
            compact
          />
        )}
      </main>
    </div>
  );
}
